import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useToast } from "../../contexts/ToastContext";
import ProviderOnboardingInvite from "../../components/ProviderOnboardingInvite";

type Role = "owner" | "admin" | "editor" | "viewer" | "photo_operator";
type ScopeType = "all_providers" | "all_merchants" | "specific_providers" | "specific_merchants";

interface Provider {
  id: string;
  name: string;
  is_active: boolean;
}

interface Merchant {
  id: string;
  shop_name: string;
  shop_domain: string;
  is_active: boolean;
}

const AccessManagement: React.FC = () => {
  const toast = useToast();
  const [role, setRole] = useState<Role>("photo_operator");
  const [scopeType, setScopeType] = useState<ScopeType>("all_providers");
  const [expiryHours, setExpiryHours] = useState<number>(168); // 7 days
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [providers, setProviders] = useState<Provider[]>([]);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  const [creating, setCreating] = useState(false);
  const [inviteLink, setInviteLink] = useState<string>("");

  // Load providers and merchants on mount
  useEffect(() => {
    const loadEntities = async () => {
      setLoadingEntities(true);
      try {
        const [provRes, merchRes] = await Promise.all([
          supabase.functions.invoke("admin-list-providers"),
          supabase.functions.invoke("admin-list-merchants"),
        ]);

        if (provRes.error) throw new Error(provRes.error.message);
        if (merchRes.error) throw new Error(merchRes.error.message);

        setProviders((provRes.data as any)?.providers || []);
        setMerchants((merchRes.data as any)?.merchants || []);
      } catch (e: any) {
        toast.error(e?.message || "Failed to load entities");
      } finally {
        setLoadingEntities(false);
      }
    };

    loadEntities();
  }, []);

  // Clear selected IDs when scope type changes
  useEffect(() => {
    setSelectedIds([]);
  }, [scopeType]);

  const handleToggleEntity = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (scopeType === "specific_providers") {
      const activeIds = providers.filter((e) => e.is_active).map((e) => e.id);
      setSelectedIds(activeIds);
    } else {
      const activeIds = merchants.filter((e) => e.is_active).map((e) => e.id);
      setSelectedIds(activeIds);
    }
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleCreate = async () => {
    setCreating(true);
    setInviteLink("");

    try {
      const payload: any = {
        role,
        scope_type: scopeType,
        expires_in_hours: expiryHours,
      };

      if (scopeType === "specific_providers" || scopeType === "specific_merchants") {
        if (selectedIds.length === 0) {
          throw new Error("Please select at least one entity");
        }
        payload.scope_ids = selectedIds;
      }

      const { data, error: invokeErr } = await supabase.functions.invoke(
        "admin-invite-create",
        { body: payload }
      );

      if (invokeErr) throw invokeErr;
      const token = (data as any)?.token;
      if (!token) throw new Error("No token received");

      const url = `${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}&type=global`;
      setInviteLink(url);
    } catch (e: any) {
      toast.error(e?.message || "Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const showEntitySelector = scopeType === "specific_providers" || scopeType === "specific_merchants";
  const entities = scopeType === "specific_providers" ? providers : merchants;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold  text-white">
            Access Management
          </h1>
          <p className="text-white/50  text-sm mt-1">
            Generate invite links to grant access across providers or merchants
          </p>
        </div>

        <div className="glass-card rounded-2xl p-6">
          {/* Form Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Scope Type */}
            <div className="flex flex-col">
              <label className="text-xs text-white/50  mb-1">
                Scope
              </label>
              <select
                className="border border-white/15  text-white  text-sm px-3 py-2"
                value={scopeType}
                onChange={(e) => setScopeType(e.target.value as ScopeType)}
              >
                <option value="all_providers">All Providers</option>
                <option value="all_merchants">All Merchants</option>
                <option value="specific_providers">Specific Providers</option>
                <option value="specific_merchants">Specific Merchants</option>
              </select>
            </div>

            {/* Role */}
            <div className="flex flex-col">
              <label className="text-xs text-white/50  mb-1">
                Role
              </label>
              <select
                className="border border-white/15  text-white  text-sm px-3 py-2"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                <option value="photo_operator">Photo Operator</option>
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>

            {/* Expiry */}
            <div className="flex flex-col">
              <label className="text-xs text-white/50  mb-1">
                Expiry
              </label>
              <select
                className="border border-white/15  text-white  text-sm px-3 py-2"
                value={String(expiryHours)}
                onChange={(e) => setExpiryHours(Number(e.target.value))}
              >
                <option value="24">1 day</option>
                <option value="72">3 days</option>
                <option value="168">7 days</option>
                <option value="720">30 days</option>
              </select>
            </div>
          </div>

          {/* Entity Selector */}
          {showEntitySelector && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-white/50 ">
                  Select {scopeType === "specific_providers" ? "Providers" : "Merchants"}
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={handleSelectAll}
                    className="text-xs text-orange-400 hover:text-orange-500 "
                  >
                    Select All Active
                  </button>
                  <span className="text-white/15">|</span>
                  <button
                    onClick={handleDeselectAll}
                    className="text-xs text-white/35 hover:text-white/70 "
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              {loadingEntities ? (
                <div className="text-white/35  text-sm py-4">
                  Loading...
                </div>
              ) : (
                <div className="border border-white/[0.08] max-h-64 overflow-y-auto">
                  {entities.length === 0 ? (
                    <div className="p-4 text-white/35  text-sm text-center">
                      No {scopeType === "specific_providers" ? "providers" : "merchants"} found
                    </div>
                  ) : (
                    entities.map((entity) => {
                      const isProvider = scopeType === "specific_providers";
                      const label = isProvider
                        ? (entity as Provider).name
                        : `${(entity as Merchant).shop_name || (entity as Merchant).shop_domain}`;
                      const sublabel = isProvider
                        ? null
                        : (entity as Merchant).shop_domain;

                      return (
                        <label
                          key={entity.id}
                          className={`flex items-center gap-3 px-4 py-2 border-b border-white/[0.06] last:border-b-0 cursor-pointer hover:bg-white/[0.03] ${
                            !entity.is_active ? "opacity-50" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(entity.id)}
                            onChange={() => handleToggleEntity(entity.id)}
                            className="w-4 h-4 text-orange-400"
                          />
                          <div className="flex-1 min-w-0">
                            <div className=" text-sm text-white truncate">
                              {label}
                            </div>
                            {sublabel && (
                              <div className=" text-xs text-white/35 truncate">
                                {sublabel}
                              </div>
                            )}
                          </div>
                          {!entity.is_active && (
                            <span className="text-xs text-white/25 ">
                              inactive
                            </span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
              )}

              {selectedIds.length > 0 && (
                <div className="mt-2 text-xs text-white/35 ">
                  {selectedIds.length} selected
                </div>
              )}
            </div>
          )}

          {/* Generate Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleCreate}
              disabled={creating}
              className="px-6 py-2 bg-orange-500 text-white border-2 border-orange-500 hover:bg-orange-400 rounded-xl transition-colors disabled:opacity-50"
            >
              {creating ? "Generating..." : "Generate Invite Link"}
            </button>
          </div>

          {/* Generated Link */}
          {inviteLink && (
            <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
              <label className="text-xs text-emerald-400  mb-2 block">
                Invite Link Generated (one-time use)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={inviteLink}
                  className="flex-1 px-3 py-2  border border-green-300 text-white  text-xs"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(inviteLink);
                  }}
                  className="px-4 py-2 border border-green-300  hover:bg-green-50  text-sm text-emerald-400"
                >
                  Copy
                </button>
              </div>
              <p className="mt-2 text-xs text-emerald-400 ">
                Share this link with the user. It will expire based on your selected duration.
              </p>
            </div>
          )}
        </div>

        {/* Provider Onboarding Invites */}
        <div className="mt-6 glass-card rounded-2xl p-6">
          <h2 className="text-lg font-bold text-white mb-1">
            Provider Onboarding Invites
          </h2>
          <p className="text-white/50 text-sm mb-4">
            Generate invite codes that providers must use to sign up. This prevents unauthorized provider signups.
          </p>
          <ProviderOnboardingInvite />
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
          <h3 className=" text-sm font-semibold text-white mb-2">
            Role Descriptions
          </h3>
          <ul className="text-xs text-white/70  space-y-1">
            <li>
              <strong>Photo Operator:</strong> Can upload and manage SKU photos
              only
            </li>
            <li>
              <strong>Viewer:</strong> Read-only access to data
            </li>
            <li>
              <strong>Editor:</strong> Can modify product data
            </li>
            <li>
              <strong>Admin:</strong> Can manage team members and settings
            </li>
            <li>
              <strong>Owner:</strong> Full access including billing
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AccessManagement;
